
import Vue from "vue";
import NavigationDrawer from "@/components/vuetify/NavigationDrawer.vue";
import ConfirmationDialog from "@/components/vuetify/ConfirmationDialog.vue";
import Pagination from "@/components/vuetify/Pagination.vue";
import Loading from "@/components/vuetify/Loading.vue";
import RadialApiClient from "@/api/RadialApiClient";
import AuthClient from "@/api/AuthClient";
import mixin from "@/mixin/mixin";
import { User } from "@/store/model";
import {
  ErpFileType,
  ErpImportLog,
  ErpImportStatus,
  ErpIntegration,
  ErpPosItemCode,
  ErpUnknownItemMode,
  ErpZeroQuantityMode,
} from "@/api/entities";
import { UpdateErpIntegrationSettingsDto } from "@/api/dto";
import dayjs from "dayjs";

/** ErpImportLog.details（JSON 文字列）の中身。backend の ErpImportLogDetails（src/integration/erp/erp.types.ts）と同じ形 */
interface ErpImportLogDetails {
  skippedByReason: Record<string, number>;
  skippedSamples: { row: number; reason: string }[];
  unknownItemCodes: string[];
  newStores: string[];
  notes: string[];
}

interface SettingsForm {
  enabled: boolean;
  posItemCode: ErpPosItemCode;
  ecEnabled: boolean;
  ecZeroQuantityMode: ErpZeroQuantityMode;
  ecUnknownItemMode: ErpUnknownItemMode;
  ecReturnSalesTypes: string;
  excludedStoreCodes: string;
  notificationEmails: string;
  saleRetentionDays: number;
}

interface SummaryCard {
  type: ErpFileType;
  label: string;
  log: ErpImportLog | null;
}

export interface DataType {
  user: User | null;
  isLoading: boolean;
  running: boolean;
  integration: ErpIntegration | null;
  incomingPath: string | null;
  latestLogs: ErpImportLog[];
  form: SettingsForm;
  savedForm: SettingsForm | null;
  logs: ErpImportLog[];
  logTotal: number;
  logPage: number;
  logPageSize: number;
  expandedId: string | null;
  expandedDetails: ErpImportLogDetails | null;
  showingAlert: boolean;
  alertMessage: string;
  alertType: string;
  posItemCodeItems: { text: string; value: ErpPosItemCode }[];
  zeroQuantityItems: { text: string; value: ErpZeroQuantityMode }[];
  unknownItemItems: { text: string; value: ErpUnknownItemMode }[];
}

const FILE_TYPE_LABEL: Record<ErpFileType, string> = {
  POS_SALE: "店舗POS売上",
  EC_SALE: "EC売上",
  STORE_STOCK: "店舗在庫",
  UNKNOWN: "不明",
};

const STATUS_LABEL: Record<ErpImportStatus, string> = {
  SUCCESS: "成功",
  PARTIAL: "一部スキップ",
  FAILED: "失敗",
  MISSING: "未着",
};

function listToText(values: string[] | undefined): string {
  return (values ?? []).join(", ");
}

function textToList(text: string): string[] {
  return text
    .split(/[,、\s]+/)
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

export default Vue.extend({
  name: "ErpIntegration",
  mixins: [mixin],
  components: {
    NavigationDrawer,
    ConfirmationDialog,
    Pagination,
    Loading,
  },
  data(): DataType {
    return {
      user: null,
      isLoading: false,
      running: false,
      integration: null,
      incomingPath: null,
      latestLogs: [],
      form: {
        enabled: true,
        posItemCode: "ITEM_NUMBER",
        ecEnabled: true,
        ecZeroQuantityMode: "IMPORT",
        ecUnknownItemMode: "SKIP",
        ecReturnSalesTypes: "",
        excludedStoreCodes: "",
        notificationEmails: "",
        saleRetentionDays: 365,
      },
      savedForm: null,
      logs: [],
      logTotal: 0,
      logPage: 1,
      logPageSize: 20,
      expandedId: null,
      expandedDetails: null,
      showingAlert: false,
      alertMessage: "",
      alertType: "success",
      posItemCodeItems: [
        { text: "品番（ロジザードの商品IDと同じコード）", value: "ITEM_NUMBER" },
        { text: "PLUコード（13桁）", value: "PLU" },
      ],
      zeroQuantityItems: [
        { text: "そのまま取り込む（数量 0）", value: "IMPORT" },
        { text: "取り込まない", value: "SKIP" },
      ],
      unknownItemItems: [
        { text: "取り込まない（件数だけ記録）", value: "SKIP" },
        { text: "取り込んで商品コードを通知する", value: "IMPORT" },
      ],
    };
  },
  computed: {
    isDirty(): boolean {
      return this.savedForm !== null && JSON.stringify(this.form) !== JSON.stringify(this.savedForm);
    },
    summaryCards(): SummaryCard[] {
      const types: ErpFileType[] = ["POS_SALE", "EC_SALE", "STORE_STOCK"];
      return types.map((type) => ({
        type,
        label: FILE_TYPE_LABEL[type],
        log: this.latestLogs.find((log) => log.fileType === type) ?? null,
      }));
    },
    lastRunText(): string {
      if (!this.integration || !this.integration.lastRunStartedAt) return "";
      const started = this.formatDate(this.integration.lastRunStartedAt);
      const ended = this.integration.lastRunEndedAt ? this.formatDate(this.integration.lastRunEndedAt) : "実行中";
      return `最終実行: ${started} 〜 ${ended}`;
    },
  },
  methods: {
    fileTypeLabel(type: ErpFileType): string {
      return FILE_TYPE_LABEL[type] ?? type;
    },
    statusLabel(status: ErpImportStatus): string {
      return STATUS_LABEL[status] ?? status;
    },
    statusColor(status: ErpImportStatus | null): string {
      switch (status) {
        case "SUCCESS":
          return "radialInfo70";
        case "PARTIAL":
          return "radialWarning";
        case "FAILED":
        case "MISSING":
          return "radialDanger70";
        default:
          return "grey";
      }
    },
    formatDate(value: Date | string | null): string {
      return value ? dayjs(value).format("YYYY/MM/DD HH:mm") : "-";
    },
    sampleRows(details: ErpImportLogDetails, reason: string): string {
      const rows = details.skippedSamples.filter((s) => s.reason === reason).map((s) => s.row);
      if (rows.length === 0) return "-";
      return rows.slice(0, 20).join(", ") + (rows.length > 20 ? " …" : "");
    },
    applyIntegration(integration: ErpIntegration | null) {
      this.integration = integration;
      if (!integration) return;
      this.form = {
        enabled: integration.enabled,
        posItemCode: integration.posItemCode,
        ecEnabled: integration.ecEnabled ?? true,
        ecZeroQuantityMode: integration.ecZeroQuantityMode,
        ecUnknownItemMode: integration.ecUnknownItemMode ?? "SKIP",
        ecReturnSalesTypes: listToText(integration.ecReturnSalesTypes),
        excludedStoreCodes: listToText(integration.excludedStoreCodes),
        notificationEmails: listToText(integration.notificationEmails),
        saleRetentionDays: integration.saleRetentionDays,
      };
      this.savedForm = { ...this.form };
    },
    async loadStatus() {
      const status = await RadialApiClient.getErpIntegrationStatus();
      if (status) {
        this.applyIntegration(status.integration);
        this.latestLogs = status.latestLogs;
        this.incomingPath = status.incomingPath;
      }
    },
    async loadLogs() {
      const list = await RadialApiClient.listErpImportLogs({ page: this.logPage, pageSize: this.logPageSize });
      if (list) {
        this.logs = list.logs;
        this.logTotal = list.total;
      }
    },
    async changeLogPage(page: number) {
      this.logPage = page;
      this.expandedId = null;
      this.expandedDetails = null;
      this.isLoading = true;
      await this.loadLogs();
      this.isLoading = false;
    },
    toggleDetail(log: ErpImportLog) {
      if (this.expandedId === log.id) {
        this.expandedId = null;
        this.expandedDetails = null;
        return;
      }
      this.expandedId = log.id;
      try {
        this.expandedDetails = log.details ? (JSON.parse(log.details) as ErpImportLogDetails) : null;
      } catch {
        this.expandedDetails = null;
      }
    },
    async createIntegration() {
      this.isLoading = true;
      const integration = await RadialApiClient.createErpIntegration({});
      if (integration) {
        await this.loadStatus();
        this.showAlert("基幹データ自動連携を有効にしました。CSV 配置先を基幹システム側に設定してください。", "success");
      } else {
        this.showAlert("エラーが発生しました。時間を空けてからもう一度お試しください。", "error");
      }
      this.isLoading = false;
    },
    async saveSettings() {
      this.isLoading = true;
      const dto: UpdateErpIntegrationSettingsDto = {
        enabled: this.form.enabled,
        posItemCode: this.form.posItemCode,
        ecEnabled: this.form.ecEnabled,
        ecZeroQuantityMode: this.form.ecZeroQuantityMode,
        ecUnknownItemMode: this.form.ecUnknownItemMode,
        ecReturnSalesTypes: textToList(this.form.ecReturnSalesTypes),
        excludedStoreCodes: textToList(this.form.excludedStoreCodes),
        notificationEmails: textToList(this.form.notificationEmails),
        saleRetentionDays: Number(this.form.saleRetentionDays) || 365,
      };
      const updated = await RadialApiClient.updateErpIntegration(dto);
      if (updated) {
        this.applyIntegration(updated);
        this.showAlert("設定を保存しました。", "success");
      } else {
        this.showAlert("設定の保存に失敗しました。", "error");
      }
      this.isLoading = false;
    },
    async runNow() {
      this.running = true;
      this.isLoading = true;
      try {
        const result = await RadialApiClient.runErpImport();
        this.showAlert(
          result ? result.message : "取込の開始に失敗しました。",
          result && result.started ? "info" : "error"
        );
        await this.loadStatus();
        await this.loadLogs();
      } catch {
        this.showAlert("取込の開始に失敗しました。", "error");
      }
      this.isLoading = false;
      this.running = false;
    },
    showAlert(message: string, type: string) {
      this.alertMessage = message;
      this.alertType = type;
      this.showingAlert = true;
    },
  },
  async mounted() {
    this.isLoading = true;
    this.user = await AuthClient.getUserInfo();
    if (this.user) {
      await this.loadStatus();
      if (this.integration) {
        await this.loadLogs();
      }
    }
    this.isLoading = false;
  },
});
